import {
    createContext,
    FC,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    createTheme,
    ThemeProvider as MuiThemeProvider,
} from "@mui/material";

import { AuthContext } from "../AuthContext";
import {
    ColorTheme,
    getColorThemeCookie,
    setColorThemeCookie,
} from "../../utils/themeCookie";

type ThemeContextValue = {
    theme: ColorTheme;
    isDarkMode: boolean;
    toggleTheme: () => void;
};

export const ThemeContext = createContext<ThemeContextValue | undefined>(
    undefined
);

export const ConnectedThemeProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { isAuthenticated } = useContext(AuthContext)!;
    const [preference, setPreference] =
        useState<ColorTheme>(getColorThemeCookie);
    const theme: ColorTheme =
        isAuthenticated && preference === "dark" ? "dark" : "light";
    const isDarkMode = theme === "dark";

    useEffect(() => {
        const root = document.documentElement;

        if (isDarkMode) {
            root.dataset.theme = "dark";
            root.style.colorScheme = "dark";
        } else {
            delete root.dataset.theme;
            root.style.colorScheme = "light";
        }

        return () => {
            delete root.dataset.theme;
            root.style.colorScheme = "light";
        };
    }, [isDarkMode]);

    const toggleTheme = () => {
        setPreference((currentTheme) => {
            const nextTheme = currentTheme === "dark" ? "light" : "dark";
            setColorThemeCookie(nextTheme);
            return nextTheme;
        });
    };

    const muiTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: theme,
                    primary: { main: "#243267" },
                    secondary: { main: "#00b69c" },
                    background:
                        theme === "dark"
                            ? { default: "#0f172a", paper: "#182338" }
                            : { default: "#f2f2f2", paper: "#ffffff" },
                    text:
                        theme === "dark"
                            ? { primary: "#f1f5f9", secondary: "#aebbd0" }
                            : { primary: "#243267", secondary: "#5a5e6a" },
                },
                components: {
                    MuiDrawer: {
                        styleOverrides: {
                            paper: {
                                backgroundColor: "#243267",
                                color: "#ffffff",
                            },
                        },
                    },
                    MuiPaginationItem: {
                        styleOverrides: {
                            root: {
                                color: theme === "dark" ? "#f1f5f9" : "#243267",
                            },
                        },
                    },
                },
            }),
        [theme]
    );

    return (
        <ThemeContext.Provider value={{ theme, isDarkMode, toggleTheme }}>
            <MuiThemeProvider theme={muiTheme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeMode = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeMode must be used inside ConnectedThemeProvider");
    }
    return context;
};
